import React from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  Stack,
  Typography,
} from 'views/components';
import {Translate, translate} from 'views/module';

import Filter from '../filter/Filter';
import NearbyCaregiverCard from './NearbyCaregiverCard';
import OutreachDialog from './OutreachDialog';

import {simplifyQuestions} from 'utils/user_bio';
import {convertToE164} from 'utils/miscellaneous/e164';

const NearbyCaregivers = (props) => {
  const {
    caregivers = [],
    applications = [],
    caregiverNameFilter,
    caregiverStatusFilter,
    languagesFilter,
    jobData,
    jobRn,
    radius,
    requester,
    setCaregiverNameFilter,
    setCaregiverStatusFilter,
    setLanguagesFilter,
    setRadius,
  } = props;
  const [selectedCaregiverRns, setSelectedCaregiverRns] = React.useState([]);
  const [isOutreachOpen, setIsOutreachOpen] = React.useState(false);
  const [outreachUpdates, setOutreachUpdates] = React.useState({});

  const defaultRadius = 10;
  const radiusOptions = [5, 10, 25, 50, 100, 200];
  const filterOptions = {
    radius: {
      key: 'radius',
      label: translate('career.jobDetail.NearbyCaregivers.Radius'),
      rank: 0,
      muted: Boolean(caregiverNameFilter),
      options: radiusOptions.reduce((options, optionRadius, index) => {
        options[optionRadius] = {
          key: optionRadius,
          label: translate('career.jobDetail.NearbyCaregivers.RadiusOption', {radius: optionRadius}),
          rank: index,
        };
        return options;
      }, {}),
      canBeEmpty: true,
      multiSelect: false,
    },
    caregiverName: {
      key: 'caregiverName',
      label: translate('caregiver.CaregiverList.Filter.CaregiverName'),
      rank: 1,
      type: 'text',
      options: null,
      canBeEmpty: false,
    },
    status: {
      key: 'status',
      label: translate('caregiver.CaregiverList.Filter.Status'),
      rank: 2,
      options: [
        'ACTIVE',
        'ENGAGED',
        'PROFILE_PENDING',
        'PROFILE_RESUME',
        'INTERVIEW_REVIEW',
        'PROFILE_REVIEW',
      ].reduce((options, status, index) => {
        options[status] = {
          key: status,
          label: translate(`caregiver.Status.${status}.Title`),
          rank: index,
        };
        return options;
      }, {}),
      muted: Boolean(caregiverNameFilter),
      canBeEmpty: false,
      multiSelect: true,
    },
    languages: {
      key: 'languages',
      label: translate('caregiver.CaregiverList.Filter.Languages'),
      rank: 3,
      options: Object.entries(translate('caregiver.CaregiverBiography.Languages.AvailableLanguages'))
          .filter(([key]) => key !== 'Other')
          .sort(([, labelA], [, labelB]) => labelA.localeCompare(labelB))
          .reduce((options, [value, label], index) => {
            options[value] = {
              key: value,
              label,
              rank: index,
            };
            return options;
          }, {}),
      muted: Boolean(caregiverNameFilter),
      canBeEmpty: false,
      multiSelect: true,
      searchable: true,
    },
  };

  const normalizeRequirement = (value) => String(value || '').trim().toUpperCase();

  const meetsLanguageRequirements = (bio, requirements) => {
    const requiredLanguages = requirements?.languages || [];
    if (requiredLanguages.length === 0) return null;

    const caregiverLanguages = new Set((bio.languages || []).map((language) => {
      return normalizeRequirement(language?.name || language);
    }));

    return requiredLanguages.every((language) => caregiverLanguages.has(normalizeRequirement(language)));
  };

  const meetsCertificationRequirements = (bio, requirements) => {
    const requiredCertifications = requirements?.certifications || [];
    const atLeastOneCertifications = requirements?.atLeastOneCertifications || [];

    if (requiredCertifications.length === 0 && atLeastOneCertifications.length === 0) return null;

    const caregiverCertifications = new Set((bio.certificates || []).map((certificate) => {
      return normalizeRequirement(certificate?.type || certificate);
    }));
    const hasRequiredCertifications = requiredCertifications.every((certification) => {
      return caregiverCertifications.has(normalizeRequirement(certification));
    });
    const hasAtLeastOneCertification = atLeastOneCertifications.length === 0 || atLeastOneCertifications.some((certification) => {
      return caregiverCertifications.has(normalizeRequirement(certification));
    });

    return hasRequiredCertifications && hasAtLeastOneCertification;
  };

  const considerationMatches = (consideration, bio, application) => {
    const applicationConfirmation = application?.answers?.considerationConfirmations?.[consideration];
    if (typeof applicationConfirmation === 'boolean') return applicationConfirmation;

    const careTasks = bio.careTasks?.selected || [];
    const preferences = bio.preferences || {};
    const availability = bio.availability || {};

    switch (consideration) {
      case 'COMFORTABLE_WITH_PETS':
        return Boolean(preferences.catAndDogComfort && preferences.catAndDogComfort !== 'NONE');
      case 'PHYSICAL_LIFTING_REQUIRED':
        return careTasks.includes('mobilitySupport') || Boolean(bio.mobilitySupport);
      case 'DEMENTIA_ALZHEIMER_EXPERIENCE':
        return careTasks.includes('memoryCare') || Boolean(bio.memoryCare);
      case 'FLEXIBLE_SCHEDULE_CHANGES':
        return Boolean(availability.willHoursChange?.response);
      default:
        return false;
    }
  };

  const meetsConsiderationRequirements = (bio, requirements, application) => {
    const considerations = requirements?.considerations || [];
    if (considerations.length === 0) return null;

    return considerations.every((consideration) => considerationMatches(consideration, bio, application));
  };

  const selection = React.useMemo(() => {
    const filter = {
      radius: {
        filterData: filterOptions.radius,
        optionSelected: radius,
      },
    };

    if (caregiverNameFilter) {
      filter.caregiverName = {
        filterData: filterOptions.caregiverName,
        optionSelected: caregiverNameFilter,
      };
    }

    if (caregiverStatusFilter.length > 0) {
      filter.status = {
        filterData: filterOptions.status,
        optionSelected: caregiverStatusFilter,
      };
    }

    if (languagesFilter.length > 0) {
      filter.languages = {
        filterData: filterOptions.languages,
        optionSelected: languagesFilter,
      };
    }

    return {
      order: {orderBy: 'distance', orderDirc: 'ASC'},
      filter,
    };
  }, [caregiverNameFilter, caregiverStatusFilter, languagesFilter, radius]);

  const nearbyCaregivers = React.useMemo(() => {
    return caregivers.map((caregiver) => {
      const bio = simplifyQuestions(caregiver.bio?.questions || {});
      const application = applications.find((item) => item.caregiverRn === caregiver.caregiverRn);
      const initialOutreach = outreachUpdates[caregiver.caregiverRn]?.initialOutreach ||
        caregiver.jobOutreach?.initialOutreach;
      const isProfileComplete = caregiver.status === 'ACTIVE';

      return {
        applicationState: application?.status || 'NOT_APPLIED',
        canTravel: isProfileComplete ? Boolean(bio.preferences?.mainTravelMethod && bio.preferences?.maxTravelTime) : null,
        caregiver,
        certificationStatus: isProfileComplete ? meetsCertificationRequirements(bio, jobData.requirements) : undefined,
        considerationStatus: isProfileComplete ? meetsConsiderationRequirements(bio, jobData.requirements, application) : undefined,
        bio,
        distance: caregiver.distance,
        initialOutreach,
        isProfileComplete,
        languageStatus: isProfileComplete ? meetsLanguageRequirements(bio, jobData.requirements) : undefined,
      };
    });
  }, [applications, caregivers, jobData, outreachUpdates]);

  const selectableCaregiverRns = React.useMemo(() => {
    return nearbyCaregivers
        .filter(({bio, initialOutreach}) => !initialOutreach && Boolean(convertToE164(bio.phoneNumber).e164Format))
        .map(({caregiver}) => caregiver.caregiverRn);
  }, [nearbyCaregivers]);

  React.useEffect(() => {
    const selectableCaregiverRnSet = new Set(selectableCaregiverRns);
    setSelectedCaregiverRns((selected) => selected.filter((caregiverRn) => selectableCaregiverRnSet.has(caregiverRn)));
  }, [selectableCaregiverRns]);

  const selectedCaregiverRnSet = React.useMemo(() => new Set(selectedCaregiverRns), [selectedCaregiverRns]);
  const selectedCaregivers = React.useMemo(() => {
    return caregivers.filter((caregiver) => selectedCaregiverRnSet.has(caregiver.caregiverRn));
  }, [caregivers, selectedCaregiverRnSet]);
  const areAllSelected = selectableCaregiverRns.length > 0 && selectedCaregiverRns.length === selectableCaregiverRns.length;
  const areSomeSelected = selectedCaregiverRns.length > 0 && !areAllSelected;

  const toggleCaregiver = (caregiverRn, isSelected) => {
    setSelectedCaregiverRns((selected) => {
      if (isSelected) return selected.includes(caregiverRn) ? selected : [...selected, caregiverRn];
      return selected.filter((selectedRn) => selectedRn !== caregiverRn);
    });
  };

  const toggleAllCaregivers = (isSelected) => {
    setSelectedCaregiverRns(isSelected ? selectableCaregiverRns : []);
  };

  const handleOutreachSent = (sent) => {
    setOutreachUpdates((current) => ({...current, ...sent}));
    setSelectedCaregiverRns((selected) => selected.filter((caregiverRn) => !sent[caregiverRn]));
  };

  const updateFilter = (filter, option) => {
    if (filter.key === 'caregiverName') {
      setCaregiverNameFilter(option?.key || '');
      return;
    }

    if (filter.key === 'status') {
      const selectedStatuses = !option ? [] : caregiverStatusFilter.includes(option.key) ?
        caregiverStatusFilter.filter((status) => status !== option.key) :
        [...caregiverStatusFilter, option.key];
      setCaregiverStatusFilter(selectedStatuses);
      return;
    }

    if (filter.key === 'languages') {
      const selectedLanguages = !option ? [] : languagesFilter.includes(option.key) ?
        languagesFilter.filter((language) => language !== option.key) :
        [...languagesFilter, option.key];
      setLanguagesFilter(selectedLanguages);
      return;
    }

    if (filter.key === 'radius') {
      setRadius(option ? Number(option.key) : defaultRadius);
    }
  };

  const resetFilter = () => {
    setCaregiverNameFilter('');
    setCaregiverStatusFilter([]);
    setLanguagesFilter([]);
    setRadius(defaultRadius);
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Divider/>
      </Grid>
      <Grid item xs={12}>
        <Stack spacing={2} width={1}>
          <Stack
            direction={{xs: 'column', md: 'row'}}
            spacing={1}
            alignItems={{xs: 'stretch', md: 'center'}}
            justifyContent="space-between"
            px={2}
          >
            <Filter
              selection={selection}
              filterOptions={filterOptions}
              updateFilter={updateFilter}
              onClearFilter={resetFilter}
            />
            {
              selectedCaregiverRns.length > 0 &&
              <Button
                mode="contained"
                onClick={() => setIsOutreachOpen(true)}
                sx={{whiteSpace: 'nowrap'}}
              >
                <Translate expression="career.jobDetail.NearbyCaregivers.RecruitingOutreach.Action" />
              </Button>
            }
          </Stack>
          <Box sx={{overflowX: 'auto', pb: 1}}>
            {
              nearbyCaregivers.length === 0 &&
              <Typography color="text.secondary" align="center">
                <Translate expression="career.jobDetail.NearbyCaregivers.Empty" />
              </Typography>
            }
            {
              nearbyCaregivers.length > 0 &&
              <Box sx={{minWidth: 1750}}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '48px 56px minmax(150px, 1.3fr) minmax(160px, 1.2fr) 145px 130px 120px 130px 130px 100px 120px 180px',
                    gap: 1.5,
                    alignItems: 'center',
                    backgroundColor: '#F9F8F6',
                    borderTop: '1px solid',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    px: 2,
                    py: 1,
                  }}
                >
                  <Checkbox
                    label=""
                    ariaLabel={areAllSelected ? 'Deselect all caregivers' : 'Select all caregivers'}
                    checked={areAllSelected}
                    disabled={selectableCaregiverRns.length === 0}
                    indeterminate={areSomeSelected}
                    onChange={toggleAllCaregivers}
                    formControlSx={{m: 0}}
                    checkBoxSx={{p: 0.5}}
                  />
                  {
                    Object.keys(translate('career.jobDetail.NearbyCaregivers.Columns')).map((column) => (
                      <Typography key={column} variant="caption" fontWeight={700}>
                        <Translate expression={`career.jobDetail.NearbyCaregivers.Columns.${column}`} />
                      </Typography>
                    ))
                  }
                </Box>
                {
                  nearbyCaregivers.map((item) => (
                    <NearbyCaregiverCard
                      key={item.caregiver.caregiverRn}
                      item={item}
                      isDisabled={Boolean(item.initialOutreach)}
                      isSelected={selectedCaregiverRnSet.has(item.caregiver.caregiverRn)}
                      onSelectionChange={(isSelected) => toggleCaregiver(item.caregiver.caregiverRn, isSelected)}
                    />
                  ))
                }
              </Box>
            }
          </Box>
        </Stack>
      </Grid>
      {
        isOutreachOpen &&
        <OutreachDialog
          caregivers={selectedCaregivers}
          jobData={jobData}
          jobRn={jobRn}
          onClose={() => setIsOutreachOpen(false)}
          onSent={handleOutreachSent}
          requester={requester}
        />
      }
    </Grid>
  );
};

NearbyCaregivers.propTypes = {
  applications: PropTypes.array.isRequired,
  caregiverNameFilter: PropTypes.string.isRequired,
  caregiverStatusFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
  languagesFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
  caregivers: PropTypes.array.isRequired,
  jobData: PropTypes.object.isRequired,
  jobRn: PropTypes.string.isRequired,
  radius: PropTypes.number.isRequired,
  requester: PropTypes.object,
  setCaregiverNameFilter: PropTypes.func.isRequired,
  setCaregiverStatusFilter: PropTypes.func.isRequired,
  setLanguagesFilter: PropTypes.func.isRequired,
  setRadius: PropTypes.func.isRequired,
};

export default React.memo(NearbyCaregivers);
